import { createContext, useContext, ReactNode } from 'react';
import EnvironmentConfig, { FeatureId } from '../common/EnvironmentConfig';

interface FeatureFlagContextData {
  enabledFeatures: Set<FeatureId>;
  isFeatureEnabled: (featureId: FeatureId) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextData | undefined>(undefined);

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const config = EnvironmentConfig.getConfig();
  const enabledFeatures = new Set<FeatureId>(config.featureFlags ?? []);

  const isFeatureEnabled = (featureId: FeatureId) => enabledFeatures.has(featureId);

  return (
    <FeatureFlagContext.Provider value={{ enabledFeatures, isFeatureEnabled }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlag(featureId: FeatureId): boolean {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlag must be used within FeatureFlagProvider');
  }
  return context.isFeatureEnabled(featureId);
}

export function useFeatureFlags(): FeatureFlagContextData {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  }
  return context;
}

export { FeatureFlagContext };
